import {
  Box,
  Grid,
  Stack,
  Typography,
  useTheme,
  IconButton,
  Avatar,
  Button,
  Badge,
  alpha,
  keyframes
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { PATHS } from 'routes/paths';
import { observer } from 'mobx-react-lite';
import { personalDataStore } from 'stores/personalDataStore';
import { chatStore } from 'stores/chatStore';
import { useMemo, useState, useEffect } from 'react';
import FeedbackDialog from 'components/FeedbackDialog';
import { API_AUTH_URL } from 'api';
import { useSocketContext } from 'contexts/SocketContext';
import { mediaStreamStore } from 'stores/mediaStreamStore';

const featuredPulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 var(--pulse-strong), 0 4px 12px var(--pulse-soft);
    transform: scale(1);
  }
  50% {
    box-shadow: 0 0 0 10px var(--pulse-clear), 0 6px 18px var(--pulse-soft);
    transform: scale(1.012);
  }
  100% {
    box-shadow: 0 0 0 0 var(--pulse-clear), 0 4px 12px var(--pulse-soft);
    transform: scale(1);
  }
`;

const featuredShimmer = keyframes`
  0% { transform: translateX(-120%) skewX(-20deg); }
  70% { transform: translateX(220%) skewX(-20deg); }
  100% { transform: translateX(220%) skewX(-20deg); }
`;

const featuredIconPulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.06); }
`;

const MainPage = () => {
  const navigate = useNavigate();
  const { user } = personalDataStore;
  const theme = useTheme();
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  useEffect(() => {
    mediaStreamStore.stopStream();
  }, []);

  const handleLogout = () => {
    personalDataStore.logout();
    navigate('/');
  };

  const { unreadCount } = useSocketContext();

  useEffect(() => {
    chatStore.fetchUnreadCount();
  }, [unreadCount]);

  useEffect(() => {
    personalDataStore.getRequestedInterviews();
  }, []);

  const menuItems = useMemo(() => {
    const items: {
      title: string;
      description: string;
      icon: string;
      path: string;
      featured: boolean;
      color: string;
      count?: number;
    }[] = [
      {
        title: 'Профиль',
        description: 'Управление личными данными и платежи',
        icon: 'mdi:account',
        path: PATHS.profile,
        featured: false,
        color: theme.palette.chart.blue[0]
      }
    ];

    if (
      !personalDataStore.requestedInterviews.length &&
      process.env.REACT_APP_IS_HR_DEMO !== 'true'
    ) {
      items.push({
        title: 'Интервью',
        description: 'Пройти собеседование с ИИ и получить рекомендации',
        icon: 'mdi:video',
        path: PATHS.videoInterview.checkDevices,
        featured: true,
        color: theme.palette.primary.main
      });
    }

    if (personalDataStore.requestedInterviews.length > 0) {
      items.push({
        title: 'Запрошенные интервью',
        description: 'Список запрошенных интервью',
        icon: 'mdi:clipboard-list',
        path: PATHS.requestedInterviews.vacancyInfo.replace(
          ':id',
          personalDataStore.requestedInterviews[0].id
        ),
        featured: false,
        color: theme.palette.chart.yellow[0],
        count: personalDataStore.requestedInterviews.length
      });
    }

    items.push({
      title: 'Мои интервью',
      description: 'Список всех интервью c результатами',
      icon: 'mdi:clipboard-list',
      path: PATHS.interviews,
      featured: false,
      color: theme.palette.chart.green[0]
    });

    if (user?.role === 'HR') {
      items.push({
        title: 'Управление вакансиями',
        description: 'Управление вакансиями компании',
        icon: 'mdi:briefcase',
        path: PATHS.hr.company,
        featured: false,
        color: theme.palette.chart.blue[0]
      });
    }

    return [
      ...items,
      {
        title: 'Сообщения',
        description: 'Личная переписка с рекрутерами и поддержкой',
        icon: 'mdi:message-text',
        path: PATHS.chat,
        featured: false,
        color: theme.palette.chart.violet?.[0] ?? theme.palette.primary.light,
        count: chatStore.unreadCount
      },
      {
        title: 'Настройки',
        description: 'Настройки приложения',
        icon: 'mdi:cog',
        path: PATHS.settings,
        featured: false,
        color: theme.palette.info.main
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    theme.palette.chart.blue,
    theme.palette.chart.green,
    theme.palette.chart.yellow,
    theme.palette.info.main,
    theme.palette.primary.main,
    user?.company,
    personalDataStore.requestedInterviews.length,
    chatStore.unreadCount
  ]);

  const fullPathAvatar = user?.avatar?.includes('http') ? user?.avatar : `${API_AUTH_URL.slice(0, -1)}${user?.avatar as string}`

  return (
    <Stack
      sx={{ height: '100%', overflow: 'auto', mr: -3, pr: 3, ml: -3, pl: 3, pb: 4 }}
      spacing={5}
    >
      <Box
        sx={{
          mt: 2,
          p: { xs: 2.5, sm: 3 },
          borderRadius: 3,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'grey.300'
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ minWidth: 0 }}>
            <Avatar
              src={fullPathAvatar}
              alt="Аватар пользователя"
              sx={{
                width: 48,
                height: 48,
                border: '1px solid',
                borderColor: 'grey.300'
              }}
              imgProps={{ referrerPolicy: 'no-referrer', crossOrigin: 'anonymous' }}
            />
            <Box sx={{ minWidth: 0 }}>
              {/* <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  fontWeight: 700,
                  fontSize: 12,
                  letterSpacing: '1px',
                  textTransform: 'uppercase',
                  color: 'text.disabled',
                  mb: 0.25
                }}
              >
                Главная
              </Typography> */}
              <Typography
                variant="h6"
                component="h1"
                fontWeight={700}
                noWrap
                sx={{ color: 'grey.900' }}
              >
                Добро пожаловать, {user?.firstName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Выберите раздел для работы
              </Typography>
            </Box>
          </Stack>
          <IconButton
            onClick={handleLogout}
            aria-label="Выйти"
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'grey.300',
              color: 'text.secondary',
              '&:hover': {
                color: 'error.main',
                borderColor: 'error.main',
                bgcolor: alpha(theme.palette.error.main, 0.08)
              }
            }}
          >
            <Icon icon="mdi:logout" width={20} height={20} />
          </IconButton>
        </Stack>
      </Box>

      <Box sx={{ flex: 1 }}>
        <Grid container spacing={2.5}>
          {menuItems.map((item) => {
            const tileColor = item.featured ? theme.palette.primary.main : item.color;
            return (
              <Grid item xs={12} sm={6} md={6} key={item.title}>
                <Box
                  onClick={() => navigate(item.path)}
                  role="button"
                  tabIndex={0}
                  sx={{
                    height: '100%',
                    cursor: 'pointer',
                    p: 2.5,
                    borderRadius: 3,
                    bgcolor: item.featured
                      ? alpha(theme.palette.primary.main, 0.06)
                      : 'background.paper',
                    border: '1px solid',
                    borderColor: item.featured ? 'primary.main' : 'grey.300',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'border-color 160ms, background-color 160ms',
                    ...(item.featured && {
                      '--pulse-strong': alpha(theme.palette.primary.main, 0.32),
                      '--pulse-soft': alpha(theme.palette.primary.main, 0.18),
                      '--pulse-clear': alpha(theme.palette.primary.main, 0),
                      animation: `${featuredPulse} 2.8s ease-in-out infinite`,
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '35%',
                        height: '100%',
                        background:
                          'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.35) 50%, rgba(255,255,255,0) 100%)',
                        animation: `${featuredShimmer} 5s ease-in-out infinite`,
                        pointerEvents: 'none'
                      },
                      '& .featured-icon': {
                        animation: `${featuredIconPulse} 2.8s ease-in-out infinite`
                      }
                    }),
                    '&:hover': {
                      borderColor: 'primary.main',
                      bgcolor: alpha(theme.palette.primary.main, 0.04)
                    },
                    '&:hover .chevron': { color: 'primary.main', transform: 'translateX(2px)' }
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Box
                      className={item.featured ? 'featured-icon' : undefined}
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 2,
                        bgcolor: alpha(tileColor, item.featured ? 1 : 0.12),
                        color: item.featured ? 'common.white' : tileColor,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        position: 'relative',
                        zIndex: 1
                      }}
                    >
                      <Icon icon={item.icon} width={24} height={24} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.25 }}>
                        <Typography
                          variant="subtitle1"
                          fontWeight={600}
                          noWrap
                          sx={{ color: 'grey.900' }}
                        >
                          {item.title}
                        </Typography>
                        {item.count ? (
                          <Badge
                            color="error"
                            badgeContent={item.count}
                            sx={{ '& .MuiBadge-badge': { position: 'static', transform: 'none' } }}
                          />
                        ) : null}
                      </Stack>
                      <Typography
                        variant={item.featured ? 'body2' : 'caption'}
                        sx={{ fontWeight: item.featured ? 600 : 400 }}
                        color={item.featured ? 'primary' : 'text.secondary'}
                      >
                        {item.description}
                      </Typography>
                    </Box>
                    <Box
                      className="chevron"
                      sx={{
                        color: 'text.disabled',
                        transition: 'color 160ms, transform 160ms',
                        display: 'flex',
                        alignItems: 'center',
                        flexShrink: 0
                      }}
                    >
                      <Icon icon="mdi:chevron-right" width={20} height={20} />
                    </Box>
                  </Stack>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Box>

      <FeedbackDialog open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />

      <Stack direction="column" alignItems="center" spacing={2} sx={{ pt: 1 }}>
        <Button
          onClick={() => setFeedbackOpen(true)}
          variant="outlined"
          startIcon={<Icon icon="mdi:headset" width={18} height={18} />}
          sx={{
            borderRadius: 1,
            textTransform: 'none',
            fontWeight: 700,
            borderColor: 'grey.300',
            color: 'text.primary',
            '&:hover': {
              borderColor: 'primary.main',
              color: 'primary.main',
              bgcolor: alpha(theme.palette.primary.main, 0.04)
            }
          }}
        >
          Написать поддержке
        </Button>
        <Typography variant="caption" sx={{ color: 'text.disabled' }}>
          {process.env.REACT_APP_SUPPORT_EMAIL}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.disabled' }}>
          YYNY © {new Date().getFullYear()}
        </Typography>
      </Stack>
    </Stack>
  );
};

export default observer(MainPage);
